import React, { useState } from 'react'
import { useTranslate } from 'react-admin'
import * as yup from 'yup'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from 'react-query'
import { Grid } from '@material-ui/core'
import ButtonWithLoader from '../../../ui/ButtonWithLoader/ButtonWithLoader'
import MyTextField from '../../fields/MyTextField'
import MySelectField from '../../fields/MySelectField/MySelectField'
import ImageCrop from '../../../ui/ImageCropComponents/ImageCrop/ImageCrop'
import addSeason from '../../../../Apis/season/addSeason'
import updateSeason from '../../../../Apis/season/updateSeason'
import QueryKeys from '../../../../utils/constants/queryKeys'
import localStorageIds from '../../../../utils/constants/localStorageIds'
import { setLocalStorageValue } from '../../../../utils/helpers/localStorageHelperMethods'
import DateRangeField from './DateRangeField/DateRangeField'
import SeasonAvatarEmptyPreview from '../../../seasonAvatarEmptyPreview/SeasonAvatarEmptyPreview'
import STATS_MODE_OPTIONS from '../../../../utils/constants/statsModeOptions'

const AddSeasonForm = (props) => {
  const { onClose, orgId, seasonData, competitionId } = props
  const isEditMode = Boolean(seasonData)
  const [croppedSeasonLogo, setCroppedSeasonLogo] = useState('')
  const translate = useTranslate()
  const queryClient = useQueryClient()

  const addSeasonMutation = useMutation((data) => addSeason(data), {
    onSuccess: ({ data }) => {
      setLocalStorageValue([{ id: localStorageIds.SELECTED_SEASON, value: data }])
      queryClient.invalidateQueries(QueryKeys.GET_SEASONS)
      onClose()
    },
  })

  const updateSeasonMutation = useMutation((data) => updateSeason(data), {
    onSuccess: ({ data }) => {
      setLocalStorageValue([{ id: localStorageIds.SELECTED_SEASON, value: data }])
      queryClient.invalidateQueries(QueryKeys.GET_SEASONS)
      onClose()
    },
  })

  const handleSubmit = (values) => {
    const payload = {
      orgId,
      name: values.name,
      startDate: values.startDate,
      endDate: values.endDate,
      ...(croppedSeasonLogo && { logoDataUri: croppedSeasonLogo }),
      ...(!isEditMode && { statsMode: values.statsMode }),
      ...(competitionId && { competitionId }),
    }

    if (isEditMode) {
      updateSeasonMutation.mutate({
        ...payload,
        seasonId: seasonData.id,
      })
    } else {
      addSeasonMutation.mutate(payload)
    }
  }

  return (
    <Formik
      initialValues={{
        name: seasonData?.name || '',
        startDate: seasonData?.startDate ? new Date(seasonData.startDate) : null,
        endDate: seasonData?.endDate ? new Date(seasonData.endDate) : null,
        ...(!isEditMode && { statsMode: 'Full' }),
      }}
      validationSchema={yup.object().shape({
        name: yup.string().required('Season name is required'),
        startDate: yup.date().nullable().required('Start date is required'),
        endDate: yup
          .date()
          .nullable()
          .required('End date is required')
          .min(yup.ref('startDate'), 'End date must be after start date'),
      })}
      onSubmit={(values) => handleSubmit(values)}
    >
      {({ setFieldValue, values }) => (
        <Form>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <Grid
              container
              spacing={2}
              xs={6}
              style={{ marginTop: 20, flexDirection: 'column', justifyContent: 'space-evenly' }}
            >
              <Grid item container justifyContent='center' style={{ marginBottom: 20 }}>
                <SeasonAvatarEmptyPreview croppedImage={croppedSeasonLogo || seasonData?.logoUrl} />
              </Grid>
              <MyTextField
                md={6}
                name='name'
                label={translate('ra.formFields.seasonName')}
                required
              />
              <DateRangeField
                startDate={values.startDate}
                endDate={values.endDate}
                setFieldValue={setFieldValue}
              />
              {!isEditMode && (
                <MySelectField
                  name='statsMode'
                  label={translate('ra.formFields.statsMode')}
                  options={STATS_MODE_OPTIONS}
                  fullWidth
                  required
                />
              )}
            </Grid>
            <div style={{ marginLeft: 70 }}>
              <Grid
                item
                container
                direction='column'
                justifyContent='center'
                alignItems='center'
                xs={6}
                style={{ marginTop: 10 }}
              >
                <ImageCrop
                  defaultImageSrc={seasonData?.logoUrl}
                  setCroppedImage={setCroppedSeasonLogo}
                  width={240}
                  height={240}
                  addImageLabel={translate('ra.buttons.uploadSeasonLogo')}
                />
              </Grid>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '5px 10px' }}>
            <ButtonWithLoader
              label={translate(isEditMode ? 'ra.buttons.update' : 'ra.buttons.add')}
              loading={addSeasonMutation.isLoading || updateSeasonMutation.isLoading}
              justifyContent='flex-end'
            />
          </div>
        </Form>
      )}
    </Formik>
  )
}

AddSeasonForm.propTypes = {
  onClose: PropTypes.func,
  orgId: PropTypes.string,
  seasonData: PropTypes.object,
  competitionId: PropTypes.string,
}

export default AddSeasonForm
